import { ClockCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { css, cx } from '@emotion/css';
import { App, Button, Checkbox, Flex, Tag, theme } from 'antd';
import type React from 'react';
import { useMemo } from 'react';
import { TestTubeColor } from '../../../components/TestTubeColor.tsx';
import type {
  ParameterSelection_ProfileFragment,
  ParameterSelection_RequestableParameterFragment,
  ParameterToAddInput,
} from '../../../graphql/generated/graphql.ts';
import { deCollator } from '../../../utils/collator.ts';
import { testTubeColorProperties, translateBillingType } from '../../../utils/enumHelpers';

type ProfileParamType = ParameterSelection_ProfileFragment['parameters'][0];

export const ProfileDetails: React.FC<{
  profile: ParameterSelection_ProfileFragment;
  requestableByMainId: Map<string, ParameterSelection_RequestableParameterFragment>;
  selectedIds: Set<string>;
  disabledIds: Set<string>;
  showLab: boolean;
  flipParamNames: boolean;
  onAdd: (paramInput: ParameterToAddInput) => void;
  onRemove: (paramId: string) => void;
  onInfo: (param: ParameterSelection_RequestableParameterFragment) => void;
}> = ({ profile, requestableByMainId, selectedIds, disabledIds, showLab, flipParamNames, onAdd, onRemove, onInfo }) => {
  const { token } = theme.useToken();
  const { message } = App.useApp();

  const styles = useMemo(
    () => ({
      container: css`
        background: ${token.colorFillQuaternary};
        border-top: 1px solid ${token.colorBorderSecondary};
        padding: ${token.paddingXS}px ${token.paddingSM}px;
        display: flex;
        flex-direction: column;
        gap: 2px;
      `,
      row: css`
        display: flex;
        align-items: center;
        gap: ${token.marginSM}px;
        padding: ${token.paddingXXS}px ${token.paddingSM}px;
        border-radius: ${token.borderRadius}px;
        cursor: pointer;
        transition: background 0.12s;
        &:hover {
          background: ${token.colorBgContainer};
        }
      `,
      rowChecked: css`
        background: ${token.colorBgContainer};
      `,
      rowDisabled: css`
        cursor: not-allowed;
        opacity: 0.45;
        &:hover {
          background: transparent;
        }
      `,
      // Kurzname: identifier — always mono + narrow fixed width, regardless of flip.
      short: css`
        font-family: ${token.fontFamilyCode};
        font-size: 14px;
        width: 96px;
        flex: none;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      `,
      // Langbezeichnung: description — always the wide, flexible column so it is never truncated.
      long: css`
        flex: 1;
        min-width: 0;
        font-size: 13.5px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      `,
      // Emphasis follows the ROLE (primary/secondary), not the content type.
      namePrimary: css`
        font-weight: 500;
        color: ${token.colorText};
      `,
      nameSecondary: css`
        font-weight: 400;
        color: ${token.colorTextSecondary};
      `,
      acute: css`
        flex: none;
        font-size: 15px;
        color: ${token.colorTextTertiary};
      `,
      lab: css`
        flex: none;
        font-size: 12px;
        color: ${token.colorTextTertiary};
        max-width: 120px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      `,
      sample: css`
        flex: none;
        display: flex;
        align-items: center;
        justify-content: center;
      `,
      empty: css`
        font-size: 13px;
        color: ${token.colorTextTertiary};
        padding: ${token.paddingSM}px;
      `,
    }),
    [token]
  );

  const handleClick = (requestableParam: ParameterSelection_RequestableParameterFragment | undefined, profileParam: ProfileParamType) => {
    if (!requestableParam) {
      message.warning('Der Parameter ist nicht verfügbar');
      return;
    }
    if (disabledIds.has(requestableParam.id)) {
      message.warning('Der Parameter ist nicht mit den bereits ausgewählten Parametern kompatibel');
      return;
    }
    if (selectedIds.has(requestableParam.id)) {
      onRemove(requestableParam.id);
      return;
    }
    onAdd({
      parameterId: requestableParam.id,
      preferredBillingType: profileParam.preferredBillingType,
      preferredDiagnoseIds: profile.diagnoses.map(it => it.id),
    });
  };

  // Sort rows alphabetically by the primary name (short by default, long when flipped).
  const sortedParams = useMemo(
    () =>
      [...profile.parameters].sort((a, b) =>
        deCollator.compare(
          flipParamNames ? a.parameter.longName : a.parameter.shortName,
          flipParamNames ? b.parameter.longName : b.parameter.shortName
        )
      ),
    [profile.parameters, flipParamNames]
  );

  if (!sortedParams.length) {
    return (
      <div className={styles.container}>
        <div className={styles.empty}>Das Profil beinhaltet keine anforderbaren Parameter</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {sortedParams.map(profileParam => {
        const requestableParam = requestableByMainId.get(profileParam.parameter.id);
        const disabled = !requestableParam || disabledIds.has(requestableParam.id);
        const checked = !!requestableParam && selectedIds.has(requestableParam.id);
        const { shortName, longName, acute } = profileParam.parameter;
        const billingType = profileParam.preferredBillingType;
        const testTubes = requestableParam?.specimens.flatMap(it => it.testTubes) ?? [];

        return (
          <div
            key={requestableParam?.id ?? profileParam.id}
            className={cx(styles.row, checked && styles.rowChecked, disabled && styles.rowDisabled)}
            onClick={() => handleClick(requestableParam, profileParam)}
          >
            <Checkbox
              checked={checked}
              disabled={disabled}
              onClick={e => e.stopPropagation()}
              onChange={() => handleClick(requestableParam, profileParam)}
            />
            {flipParamNames ? (
              <>
                <span className={cx(styles.long, styles.namePrimary)}>{longName}</span>
                <span className={cx(styles.short, styles.nameSecondary)}>{shortName}</span>
              </>
            ) : (
              <>
                <span className={cx(styles.short, styles.namePrimary)}>{shortName}</span>
                <span className={cx(styles.long, styles.nameSecondary)}>{longName}</span>
              </>
            )}
            {acute && <ClockCircleOutlined className={styles.acute} />}
            {billingType && <Tag style={{ marginInlineEnd: 0, color: token.colorTextSecondary }}>{translateBillingType(billingType)}</Tag>}
            {showLab && <span className={styles.lab}>{profileParam.parameter.lab.shortName}</span>}
            <span className={styles.sample}>
              <Flex gap={4} align="center">
                {testTubes.map((tt, i) => (
                  <TestTubeColor key={`${tt.id}_${i}`} withText={false} properties={testTubeColorProperties(tt.color)} />
                ))}
              </Flex>
            </span>
            <Button
              icon={<InfoCircleOutlined />}
              disabled={!requestableParam}
              type="text"
              size="small"
              onClick={e => {
                e.stopPropagation();
                if (requestableParam) {
                  onInfo(requestableParam);
                }
              }}
            />
          </div>
        );
      })}
    </div>
  );
};
